/**
 * Pairing & setup-code card. Regenerating the room's 8-char LONG_LIVED TV setup
 * code invalidates the current one and forces the TV to re-pair — so it is gated
 * behind a confirm modal. The whole capability is server-flagged: when the
 * pairing service can't yet mint an admin-scoped code for an explicit room, the
 * control honestly degrades to a disabled state with an explanation (never a fake
 * code), and only "Copy room id" remains.
 */

import { useState } from 'react';
import styled from 'styled-components';
import Button from '@8x8/oxygen-button';
import { CopyToClipboard } from '@8x8/oxygen-copy-to-clipboard';
import type { PairingCode, Room } from '@spaces/contracts';
import { resetPairingCode } from '../../api';
import { fmtTime } from '../../ui/format';
import { useToast } from '../../toast';
import { ConfirmModal } from '../../ui/ConfirmModal';
import { Card, CardTitle, Muted, Notice } from '../../ui/primitives';

const Reveal = styled.div`
    border: 1px solid ${p => `${p.theme.action08}33`};
    background: ${p => `${p.theme.action08}0F`};
    border-radius: 8px;
    padding: 14px 16px;
    margin-bottom: 14px;
`;

const RevealLabel = styled.div`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: ${p => p.theme.action08};
    margin-bottom: 6px;
`;

const Code = styled.span`
    font-family: ui-monospace, Menlo, Consolas, monospace;
    font-size: 24px;
    font-weight: 700;
    letter-spacing: 0.14em;
    color: ${p => p.theme.textColor01};
`;

const Desc = styled.div`
    font-size: 13px;
    line-height: 19px;
    color: ${p => p.theme.textColor02};
    margin-bottom: 14px;
`;

export function PairingCard({ room, canReset }: { room: Room; canReset: boolean }): JSX.Element {
    const toast = useToast();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [code, setCode] = useState<PairingCode | null>(null);

    const regenerate = async (): Promise<void> => {
        setBusy(true);
        const result = await resetPairingCode(room.id);

        setBusy(false);
        setConfirmOpen(false);

        if (!result.ok) {
            toast.notify('error', `Couldn't regenerate: ${result.message}`);

            return;
        }

        setCode(result.data.setupCode);
        toast.notify('success', 'Setup code regenerated.');
    };

    return (
        <Card>
            <CardTitle>Pairing &amp; setup code</CardTitle>

            {canReset ? (
                <>
                    {code ? (
                        <Reveal>
                            <RevealLabel>NEW SETUP CODE</RevealLabel>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                <Code>{code.code}</Code>
                                <CopyToClipboard
                                    textToCopy={code.code}
                                    onCopySuccess={() => toast.notify('success', 'Setup code copied.')}
                                >
                                    Copy
                                </CopyToClipboard>
                            </div>
                            <div style={{ marginTop: 8 }}>
                                <Muted style={{ fontSize: 12 }}>
                                    Expires {fmtTime(code.expiresAt)} · the previous code no longer works.
                                </Muted>
                            </div>
                        </Reveal>
                    ) : null}
                    <Desc>
                        The 8-character setup code is typed into the TV at <code>/setup</code>. Regenerating
                        invalidates the current code and forces the TV to re-pair.
                    </Desc>
                    <Button variant="primary" onClick={() => setConfirmOpen(true)}>Regenerate setup code</Button>

                    <ConfirmModal
                        open={confirmOpen}
                        title="Regenerate the setup code?"
                        confirmLabel="Regenerate"
                        destructive
                        busy={busy}
                        onClose={() => setConfirmOpen(false)}
                        onConfirm={() => void regenerate()}
                    >
                        The current code stops working immediately and the TV must be re-paired with the new code.
                    </ConfirmModal>
                </>
            ) : (
                <>
                    <div style={{ marginBottom: 14 }}>
                        <Notice kind="warning">
                            Per-room setup-code reset is awaiting a pairing-service update (admin-scoped mint
                            with an explicit room). It&apos;s disabled here to avoid issuing a code scoped to the
                            wrong room.
                        </Notice>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                        <Button variant="primary" isDisabled>Regenerate setup code</Button>
                    </div>
                    <CopyToClipboard
                        textToCopy={room.id}
                        onCopySuccess={() => toast.notify('success', 'Room id copied.')}
                    >
                        Copy room id
                    </CopyToClipboard>
                </>
            )}
        </Card>
    );
}
